'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useOnceInView } from '@elem/compPage/on-the-road-power-solution/pages/product/components/US/useOnceInView';
import EcoImg from 'eco-design/base/eco-img';
import EcoDesignEventsBus from 'eco-design-micro/EcoDesignEventsBus';
import { ENUM_ECO_CART_EVENT, ItfEcoCartItem } from 'eco-design/elements/eco-cart/type.d';
import { useLang } from '@/i18n';
import { getSolarRecommand } from '@/service/solar-calculator';
import './index.scss';
import FormatCurrencyLocal from 'eco-design-reliance/currency/FormatCurrencyLocal';
import EcoLink from 'eco-design/base/eco-link';

interface ItfItem {
    badge: string;
    name: string;
    specs: string;
    price?: number | string;
    originPrice: string;
    displayPrice?: string;
    displayOriginPrice?: string;
    pcImg: string;
    h5Img: string;
    productId: string;
    variantId: string;
    inventory?: number;
    inventoryPolicy?: ItfEcoCartItem['inventoryPolicy'];
    compareAtPrice?: number;
    img?: string;
    variantImg?: string;
    productTitle?: string;
    skuTitle?: string;
    __delisted?: boolean;
}

const productImg = 'https://websiteoss.ecoflow.com/figma-uploads/1784951897249-ba73vb-compressed-BKW3-5%E5%BA%A6%E7%94%B5AC-45%E5%BA%A6%E5%8F%B3%201.webp';

const ProductList: React.FC = () => {
    const { t } = useLang('stream.overview');
    const { locale = 'nl' } = useRouter();

    useOnceInView('.stream-nl-product-list .title', () => {
        document.querySelector('.stream-nl-product-list .title')?.classList.add('enter');
        document.querySelector('.stream-nl-product-list .items-pc')?.classList.add('enter');
        document.querySelector('.stream-nl-product-list .items-h5')?.classList.add('enter');
    });

    const INITIAL_ITEMS: ItfItem[] = [
        {
            badge: t('product_list_item_1_badge'),
            name: t('product_list_item_1_name'),
            specs: t('product_list_item_1_specs'),
            price: '€1.399,00',
            originPrice: '€1.699,00',
            pcImg: productImg,
            h5Img: 'https://websiteoss.ecoflow.com/figma-uploads/1784964505094-q33kd5-compressed-AC%205001.webp',
            productId: '10265308135700',
            variantId: '52686355955988',
        },
        {
            badge: t('product_list_item_2_badge'),
            name: t('product_list_item_2_name'),
            specs: t('product_list_item_2_specs'),
            price: '€2.598,00',
            originPrice: '€3.298,00',
            pcImg: productImg,
            h5Img: 'https://websiteoss.ecoflow.com/figma-uploads/1784964511543-6bcry4-compressed-AC%205000%2BEB.webp',
            productId: '10265308135700',
            variantId: '52686355988756',
        },
        {
            badge: t('product_list_item_3_badge'),
            name: t('product_list_item_3_name'),
            specs: t('product_list_item_3_specs'),
            price: '€3.797,00',
            originPrice: '€4.897,00',
            pcImg: productImg,
            h5Img: 'https://websiteoss.ecoflow.com/figma-uploads/1784964514954-kjvvjo-compressed-AC%205000%2BEB*2.webp',
            productId: '10265308135700',
            variantId: '52686356021524',
        },
    ];

    const [items, setItems] = useState<ItfItem[]>(INITIAL_ITEMS);

    useEffect(() => {
        const fetchItems = async () => {
            const nextItems = await Promise.all(
                INITIAL_ITEMS.map(async (item) => {
                    try {
                        const res = await getSolarRecommand({ productId: item.productId, variantId: item.variantId }, locale);
                        if (res?.code === '0') {
                            console.log(item.variantId, res?.data);
                            const isDelisted = res?.data && typeof res.data === 'object' && Object.keys(res.data).length === 0;
                            if (isDelisted) {
                                return { ...item, __delisted: true };
                            }
                            return {
                                ...item,
                                ...res?.data,
                                displayPrice: FormatCurrencyLocal(res?.data.price, locale),
                                displayOriginPrice: FormatCurrencyLocal(res?.data.compareAtPrice, locale),
                                inventory: res?.data?.inventoryQuantity,
                                inventoryPolicy: res?.data?.inventoryPolicy,
                            };
                        }
                    } catch (e) {
                        console.error(e);
                    }
                    return item;
                }),
            );
            setItems(nextItems.filter((item) => !item.__delisted));
        };

        fetchItems();
    }, [locale]);

    const handleAddToCart = (item: ItfItem) => {
        const addCarts: ItfEcoCartItem[] = [
            {
                compareAtPrice: Number(item.compareAtPrice || 0),
                price: Number(item.price || 0),
                inventoryQuantity: item.inventory ?? 0,
                inventoryPolicy: item.inventoryPolicy || 'deny',
                productId: item.productId,
                variantId: item.variantId,
                img: item.img || item.h5Img,
                variantImg: item.variantImg || item.h5Img,
                productTitle: item.productTitle || t(item.name),
                skuTitle: item.skuTitle || t(item.name),
                quantity: 1,
            },
        ];
        EcoDesignEventsBus.emit(ENUM_ECO_CART_EVENT.ECO_CART_ADD, addCarts);
    };

    const renderCard = (item: ItfItem, i: number) => {
        const isSoldOut = Number(item.inventory || 0) <= 0 && item.inventoryPolicy !== 'continue';
        const buyLink = `https://${locale}.ecoflow.com/products/stream-ac-solar-battery?variantId=${item.variantId}`;
        const productName = item.skuTitle || item.name;
        const position = t('produtct_list_title');
        console.log('item', item);
        return (
            <>
                <EcoImg className="item-img" pc={item.h5Img} h5={item.h5Img} alt={item.name} />
                <div className="item-info">
                    <p className="item-badge">{item.badge}</p>
                    <h3 className="item-name">{item?.skuTitle || item.name}</h3>
                    <p className="item-specs">{item.specs}</p>
                    <p className="item-price">
                        <span>{item.displayPrice || item.price}</span>
                        <del>{item.displayOriginPrice || item.originPrice}</del>
                    </p>
                    <div className="item-actions">
                        <EcoLink
                            className={`item-button item-button-cart${isSoldOut ? ' is-disabled' : ''}`}
                            onClick={isSoldOut ? undefined : () => handleAddToCart(item)}
                            dataLayer={
                                isSoldOut
                                    ? undefined
                                    : {
                                          event: 'uaEvent',
                                          event_name: 'add_to_cart',
                                          product_name: productName,
                                          position,
                                          view_page_type: 'PDP',
                                          product_id: item.productId,
                                          varient_id: item.variantId,
                                          __btn_id1: `bkw-gen2-NL1 item${i} .btn-2`,
                                          __btn_id2: t('product_list_add_to_cart'),
                                          __btn_type: 'button',
                                      }
                            }
                        >
                            {t('product_list_add_to_cart')}
                        </EcoLink>
                        <EcoLink
                            className={`item-button item-button-buy${isSoldOut ? ' is-disabled' : ''}`}
                            href={isSoldOut ? undefined : buyLink}
                            target="_blank"
                            dataLayer={
                                isSoldOut
                                    ? undefined
                                    : {
                                          event: 'uaEvent',
                                          event_name: 'buy',
                                          product_name: productName,
                                          position,
                                          view_page_type: 'PDP',
                                          product_id: item.productId,
                                          varient_id: item.variantId,
                                          __btn_id1: `bkw-gen2-NL1 item${i} .btn-1`,
                                          __btn_id2: buyLink,
                                          __btn_type: 'href',
                                      }
                            }
                        >
                            {isSoldOut ? t('product_list_sold_out') : t('product_list_buy_now')}
                        </EcoLink>
                    </div>
                </div>
            </>
        );
    };

    return (
        <div className="stream-nl-product-list GtmModuleData-section space" id="stream-nl-product-list" data-section-id="stream-nl-product-list">
            <div className="info">
                <h2 className="title ani-1">{t('produtct_list_title')}</h2>
            </div>
            <ul className="items box items-pc ani-2">
                {items?.map((item, i) => (
                    <li className="item" key={item.variantId}>
                        {renderCard(item, i)}
                    </li>
                ))}
            </ul>
            <div className="swiper-box">
                <Swiper slidesPerView={'auto'} spaceBetween={12} className="items box items-h5 ani-2">
                    {items?.map((item, i) => (
                        <SwiperSlide className="item" key={item.variantId}>
                            {renderCard(item, i)}
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>
        </div>
    );
};

export default ProductList;
